import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ChevronRight, Phone, CheckCircle } from "lucide-react";

const processSteps = [
  {
    step: 1,
    title: "Get in Touch",
    description: "Call, text or fill in the contact form. Tell me what you need doing and I'll get back to you quickly."
  },
  {
    step: 2,
    title: "Get a Quote",
    description: "For straightforward jobs, I can often quote over the phone or from a few photos. For bigger or more complex work, I'll pop round to take a look - no charge."
  },
  {
    step: 3,
    title: "Book the Work",
    description: "If you're happy with the quote, we agree a date that works for you. I'll confirm a day or two before."
  },
  {
    step: 4,
    title: "Job Done",
    description: "I turn up on time, do the work properly, clean up after myself, and make sure you're happy before I leave."
  }
];

const ProcessPage = () => {
  return (
    <div>
      <Helmet>
        <title>How It Works | Getting a Quote | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="Simple process from enquiry to job done. Free quotes, clear pricing, reliable service. Door hanging, trimwork, panelling in Greater Manchester. Call 07300 405045." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/process" />
      </Helmet>
      
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Simple & Straightforward</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            How It Works
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            No fuss, no jargon. Here's how it works from first contact to job done.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="space-y-0">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                {index < processSteps.length - 1 && (
                  <div className="absolute left-6 md:left-7 top-16 md:top-20 w-0.5 h-full bg-gradient-to-b from-[#2D5A4A] to-[#2D5A4A]/20" />
                )}
                
                <div className="flex gap-5 md:gap-8 pb-10 md:pb-12">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#2D5A4A] text-white rounded-full flex items-center justify-center font-bold text-lg md:text-xl shadow-lg">
                      {step.step}
                    </div>
                  </div>
                  
                  <div className="flex-1 pt-1 md:pt-2">
                    <h3 className="text-xl md:text-2xl font-bold text-[#3A3A3A] mb-2 md:mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 md:mt-10 text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black h-14 px-10 text-lg font-bold w-full sm:w-auto">
                Get Started
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">What to Expect</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A]">
              Working With Me
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              "Clear communication - I'll keep you informed",
              "Turn up when I say I will",
              "Realistic timescales - no false promises",
              "Tidy work - I clean up as I go",
              "Fair pricing - no hidden extras",
              "12-month guarantee on all work"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 bg-white p-4 md:p-5 rounded-xl shadow-sm">
                <div className="w-10 h-10 bg-[#2D5A4A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-[#2D5A4A]" />
                </div>
                <span className="text-[#3A3A3A] font-medium text-base">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">Common Questions</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A]">
              FAQs
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { q: "Do you supply materials?", a: "I can do, or you can supply your own. Either works. If you want me to get materials, I'll include that in the quote." },
              { q: "How long does a job take?", a: "Depends on the job. A single door might take a couple of hours. Full room of skirting could be a day. I'll give you a realistic idea when I quote." },
              { q: "What areas do you cover?", a: "Greater Manchester and surrounding areas. If you're not sure, just ask." },
              { q: "Do I need to be home?", a: "Not necessarily. As long as I can get access and you're happy for me to crack on, that's fine." },
              { q: "When do I pay?", a: "Payment on completion, once you're happy with the work. Cash or bank transfer." }
            ].map((faq, i) => (
              <div key={i} className="bg-[#f8f7f5] p-5 rounded-xl">
                <h3 className="font-bold text-[#3A3A3A] mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-28 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5">
            Ready to Get Started?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8">
            Get in touch for a free, no-obligation quote.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/contact" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-14 px-10 text-lg w-full" data-testid="process-get-quote-btn">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold h-14 px-10 text-lg w-full">
                <Phone className="mr-2 h-6 w-6" />
                Call 07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
